import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { logger } from 'src/analytics/KatalLogger';
import { getMasterSegments } from 'src/api/app-sync-services';
import { CorpSegmentNames, SegmentCategoryType } from 'src/constants/corp-segment-constants';
import {
  fetchCorpSegmentDataFromS3
} from 'src/features/business-group/forecast-template/forecast-utils/CorpSegmentsUtils';
import { MasterBusinessSegments, MasterCorpSegments } from 'src/models/AppContextModels';
import { Status } from 'src/models/AuthContextModels';
import { CostCenterAndProductLineValuesPerBusinessGroup, ForecastTemplateMasterCorpSegmentDropdownValues } from 'src/models/ForecastModels';
import { RootState } from '../store';
import { fetchCCAndPLDropdownValuesOfAllActiveBusinessGroups, fetchMasterCorpSegmentDropdownValues } from '../thunks/corpSegmentThunks';

/**
 * Segments needed for homepage initialization (Stage 4: CC+PL dropdowns → Stage 5: authorization).
 * Only Cost Center and Product Line are required to unblock the homepage.
 */
const CRITICAL_SEGMENTS: Set<string> = new Set([CorpSegmentNames.COST_CENTER, CorpSegmentNames.PRODUCT_LINE]);

interface CorpSegmentState {
  // Master Corp Segments in Hierarchy Representation
  masterCorpSegments: MasterCorpSegments[];
  masterCorpSegmentsStatus: Status;

  // Deferred (non-critical) segments loading status — BG wrapper awaits this
  deferredCorpSegmentsStatus: Status;

  // Master Business Segments in Hierarchy Representation
  masterBusinessSegmentsStatus: Status;
  masterBusinessSegments: MasterBusinessSegments[];

  // Selected CC and PLs in corresponding Business Group
  masterCostCenterDropdownValuesForAllBusinessGroups: CostCenterAndProductLineValuesPerBusinessGroup[];

  // Once user is in Any of Business Groups.
  // Selected CC & PL & remaining Corp Segments are from Master.
  masterCorpSegmentDropdownValuesStatus: Status;
  masterCorpSegmentDropdownValues: ForecastTemplateMasterCorpSegmentDropdownValues[];
}

export const corpSegmentInitialState: CorpSegmentState = {
  masterCorpSegments: [],
  masterCorpSegmentsStatus: Status.NotInitiated,

  deferredCorpSegmentsStatus: Status.NotInitiated,

  masterBusinessSegments: [],
  masterBusinessSegmentsStatus: Status.NotInitiated,

  masterCostCenterDropdownValuesForAllBusinessGroups: [],

  masterCorpSegmentDropdownValuesStatus: Status.NotInitiated,
  masterCorpSegmentDropdownValues: []
};

/**
 * Phase 1 (Critical): Fetches master segment metadata from AppSync, then downloads
 * only Cost Center and Product Line S3 files. These are the only segments needed
 * for homepage initialization (CC+PL dropdowns → user authorization).
 *
 * Non-critical segments (Project, Location, Account, Channel, Company) are stored
 * with empty hierarchy data and loaded in Phase 2.
 */
export const fetchCriticalMasterSegments = createAsyncThunk(
  'corpSegment/fetchCriticalMasterSegments',
  async (_, { rejectWithValue }) => {
    try {
      const masterSegmentsResponse = await getMasterSegments();

      const allCorpSegmentMeta = masterSegmentsResponse?.filter(
        (segment) => segment.segment_category === SegmentCategoryType.CORP
      ) as MasterCorpSegments[];

      const businessSegments = masterSegmentsResponse?.filter(
        (segment) => segment.segment_category === SegmentCategoryType.BUSINESS
      ) as MasterBusinessSegments[];

      // Split into critical (download now) and deferred (download later)
      const criticalMeta: MasterCorpSegments[] = [];
      const deferredMeta: MasterCorpSegments[] = [];
      for (const segment of allCorpSegmentMeta) {
        if (CRITICAL_SEGMENTS.has(segment.segment_name)) {
          criticalMeta.push(segment);
        } else {
          deferredMeta.push(segment);
        }
      }

      // Download S3 data only for critical segments
      const criticalSegments = await Promise.all(criticalMeta.map(fetchCorpSegmentDataFromS3));

      // Deferred segments get metadata only — no S3 download yet
      const deferredSegments: MasterCorpSegments[] = deferredMeta.map((segment) => ({
        ...segment,
        segment_hierarchy_data_from_s3: []
      }));

      return {
        corpSegments: [...criticalSegments, ...deferredSegments],
        businessSegments,
        deferredSegmentNames: deferredMeta.map((s) => s.segment_name)
      };
    } catch (error: unknown) {
      logger.error('Error fetching critical master segments:', error as Record<string, unknown>);
      return rejectWithValue('Failed to fetch critical master segments');
    }
  }
);

/**
 * Phase 2 (Deferred): Downloads S3 data for non-critical segments (Project 17MB,
 * Location 4.8MB, Account, Channel, Company). Runs in background after homepage renders.
 *
 * Merges downloaded data into the existing masterCorpSegments array in Redux.
 * BG wrapper awaits this before rendering BG-level routes.
 */
export const fetchDeferredCorpSegments = createAsyncThunk(
  'corpSegment/fetchDeferredCorpSegments',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const currentSegments = state.corpSegmentsStore.masterCorpSegments;

      // Find segments that still have empty hierarchy data (not yet downloaded)
      const segmentsToLoad = currentSegments.filter(
        (s) => !CRITICAL_SEGMENTS.has(s.segment_name) && s.segment_hierarchy_data_from_s3.length === 0
      );

      if (segmentsToLoad.length === 0) {
        return []; // Already loaded (e.g., duplicate dispatch)
      }

      const loadedSegments = await Promise.all(segmentsToLoad.map(fetchCorpSegmentDataFromS3));
      return loadedSegments;
    } catch (error: unknown) {
      logger.error('Error fetching deferred corp segments:', error as Record<string, unknown>);
      return rejectWithValue('Failed to fetch deferred corp segments');
    }
  }
);

const corpSegmentSlice = createSlice({
  name: 'corpSegment',
  initialState: corpSegmentInitialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Phase 1: Critical segments (CC + PL with S3 data, others with metadata only)
      .addCase(fetchCriticalMasterSegments.pending, (state) => {
        state.masterCorpSegments = [];
        state.masterBusinessSegments = [];
        state.masterCorpSegmentsStatus = Status.Loading;
        state.masterBusinessSegmentsStatus = Status.Loading;
        state.deferredCorpSegmentsStatus = Status.NotInitiated;
      })
      .addCase(fetchCriticalMasterSegments.fulfilled, (state, action) => {
        state.masterCorpSegments = action.payload.corpSegments;
        state.masterBusinessSegments = action.payload.businessSegments;
        state.masterCorpSegmentsStatus = Status.Completed;
        state.masterBusinessSegmentsStatus = Status.Completed;
        // Mark deferred as loading if there are segments to load
        state.deferredCorpSegmentsStatus = action.payload.deferredSegmentNames.length > 0
          ? Status.Loading
          : Status.Completed;
      })
      .addCase(fetchCriticalMasterSegments.rejected, (state) => {
        state.masterCorpSegments = [];
        state.masterBusinessSegments = [];
        state.masterCorpSegmentsStatus = Status.Failed;
        state.masterBusinessSegmentsStatus = Status.Failed;
        state.deferredCorpSegmentsStatus = Status.Failed;
      })

      // Phase 2: Deferred segments — merge S3 data into existing masterCorpSegments
      .addCase(fetchDeferredCorpSegments.pending, (state) => {
        // Don't overwrite Failed from a critical-phase rejection — the finally block
        // always dispatches this thunk, and we must not mask the upstream failure.
        if (state.masterCorpSegmentsStatus !== Status.Failed) {
          state.deferredCorpSegmentsStatus = Status.Loading;
        }
      })
      .addCase(fetchDeferredCorpSegments.fulfilled, (state, action) => {
        if (action.payload.length === 0) {
          return; // Nothing loaded — preserve current status (may be Failed from critical phase)
        }
        const loadedMap = new Map(action.payload.map((s) => [s.segment_name, s]));
        state.masterCorpSegments = state.masterCorpSegments.map((existing) => {
          const loaded = loadedMap.get(existing.segment_name);
          return loaded ? loaded : existing;
        });
        state.deferredCorpSegmentsStatus = Status.Completed;
      })
      .addCase(fetchDeferredCorpSegments.rejected, (state) => {
        state.deferredCorpSegmentsStatus = Status.Failed;
      })

      // fetchCCAndPLDropdownValuesOfAllBusinessGroups
      .addCase(fetchCCAndPLDropdownValuesOfAllActiveBusinessGroups.fulfilled, (state, action) => {
        state.masterCostCenterDropdownValuesForAllBusinessGroups = action.payload;
      })
      .addCase(fetchCCAndPLDropdownValuesOfAllActiveBusinessGroups.rejected, (state) => {
        state.masterCostCenterDropdownValuesForAllBusinessGroups = [];
      })

      // fetchMasterCorpSegmentDropdownValues
      .addCase(fetchMasterCorpSegmentDropdownValues.pending, (state, action) => {
        state.masterCorpSegmentDropdownValues = [];
        state.masterCorpSegmentDropdownValuesStatus = Status.Loading;
      })
      .addCase(fetchMasterCorpSegmentDropdownValues.fulfilled, (state, action) => {
        state.masterCorpSegmentDropdownValues = action.payload;
        state.masterCorpSegmentDropdownValuesStatus = Status.Completed;
      })
      .addCase(fetchMasterCorpSegmentDropdownValues.rejected, (state) => {
        state.masterCorpSegmentDropdownValues = [];
        state.masterCorpSegmentDropdownValuesStatus = Status.Failed;
      });
  }
});

export default corpSegmentSlice.reducer;
